import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import {
    Alert,
    Icon,
    Badge
} from '../../components/ComponentLibrary';
import {
    Calendar,
    User,
    Phone,
    Mail,
    Clock,
    CheckCircle2,
    XCircle,
    MessageSquare,
    Filter,
    RefreshCw,
    Check,
    X,
    MessageCircle
} from 'lucide-react';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


export default function AdminFreeSessions() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all");
    const [updating, setUpdating] = useState(null);

    // Confirm Modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/free-sessions');
            setSessions(response.data.data);
            setError("");
        } catch (err) {
            setError("Failed to load registrations. " + (err.response?.data?.error || ""));
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        setUpdating(id);
        try {
            await api.patch(`/admin/free-sessions/${id}`, { status: newStatus });
            setSessions(sessions.map(s => s._id === id ? { ...s, status: newStatus } : s));
        } catch (err) {
            showAlert('Update Failed', 'Failed to update lead status', 'danger');
        } finally {
            setUpdating(null);
        }
    };


    const filteredSessions = sessions.filter(s => filter === "all" || s.status === filter);

    const getStatusVariant = (status) => {
        switch (status) {
            case 'completed': return 'status-active';
            case 'contacted': return 'status-pending';
            case 'cancelled': return 'status-inactive';
            default: return 'status-pending';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'completed': return 'Completed';
            case 'contacted': return 'Contacted';
            case 'cancelled': return 'Cancelled';
            default: return 'Waiting';
        }
    };

    const timeLabels = {
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        night: 'Night'
    };

    if (loading && sessions.length === 0) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div className="spinner"></div></div>;
    }

    return (
        <div className="pipeline-container">
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>Sessions Central</h1>
                    <p>Managing prospective student leads and intake modules</p>
                </div>
                <button className="btn-action primary" onClick={fetchSessions}>
                    <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                    Refresh Pipeline
                </button>
            </header>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
                <button className={`btn-action ${filter === 'all' ? 'primary' : ''}`} onClick={() => setFilter('all')}>
                    Show All ({sessions.length})
                </button>
                <button className={`btn-action ${filter === 'pending' ? 'primary' : ''}`} onClick={() => setFilter('pending')}>
                    Waiting
                </button>
                <button className={`btn-action ${filter === 'contacted' ? 'primary' : ''}`} onClick={() => setFilter('contacted')}>
                    In Contact
                </button>
                <button className={`btn-action ${filter === 'completed' ? 'primary' : ''}`} onClick={() => setFilter('completed')}>
                    Successful
                </button>
            </div>

            {error && <Alert variant="error" style={{ marginBottom: '2.5rem' }}>{error}</Alert>}

            {filteredSessions.length === 0 ? (
                <div className="admin-table-section-card" style={{ textAlign: 'center', padding: '6rem 0', color: '#94a3b8', fontStyle: 'italic' }}>
                    No leads matching the current criteria were found in the archive.
                </div>
            ) : (
                <div className="admin-grid-cols-3">
                    {filteredSessions.map((session) => (
                        <div key={session._id} className="admin-table-section-card" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem' }}>
                                <span className={`status-pill ${getStatusVariant(session.status)}`}>
                                    {getStatusLabel(session.status).toUpperCase()}
                                </span>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8' }}>
                                    {new Date(session.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </span>
                            </div>

                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <User size={20} color="#0f172a" />
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>{session.fullName}</h3>
                                        <p style={{ fontSize: '0.65rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{session.age} YEAR OLD PROSPECT</p>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', fontSize: '0.9rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                                        <Phone size={14} color="#94a3b8" /> <span dir="ltr">{session.phone}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                                        <Mail size={14} color="#94a3b8" /> <span>{session.email}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                                        <Clock size={14} color="#94a3b8" /> <span>Shift: {timeLabels[session.preferredTime] || "Flexible"}</span>
                                    </div>
                                </div>

                                {session.goals && (
                                    <div style={{
                                        padding: '1.25rem',
                                        background: '#f8fafc',
                                        borderRadius: '0.875rem',
                                        fontSize: '0.875rem',
                                        color: '#334155',
                                        lineHeight: 1.6,
                                        border: '1px solid #f1f5f9'
                                    }}>
                                        <div style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 900, textTransform: 'uppercase', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>Aspirations</div>
                                        "{session.goals}"
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                                {session.status === 'pending' && (
                                    <button className="btn-action primary" style={{ flex: 1 }} onClick={() => updateStatus(session._id, 'contacted')}>
                                        <MessageCircle size={14} />
                                        Contact
                                    </button>
                                )}
                                {session.status === 'contacted' && (
                                    <button className="btn-action primary" style={{ flex: 1, border: 'none', background: '#15803d' }} onClick={() => updateStatus(session._id, 'completed')}>
                                        <Check size={14} />
                                        Complete
                                    </button>
                                )}
                                <button className="btn-action" style={{ color: '#dc2626', borderColor: '#fee2e2' }} onClick={() => updateStatus(session._id, 'cancelled')}>
                                    <X size={14} />
                                    Drop
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>

    );
}
